import { Component, Inject } from '@angular/core';

import { MatDividerModule } from '@angular/material/divider';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AgentsService } from 'src/app/core/services/agents.service';
import { RecruitmentUser as User } from '@models/domains/recruitment';
import { FileControlComponent } from 'src/app/shared/components/file-control/file-control.component';
import { noWhitespaceValidator } from 'src/app/shared/utils/no-whitespace-validator';
import type { GetAdviceDialogData, GetAdviceFormValue, GetAdviceFormControls, GetAdviceResult } from './get-advice.types';

@Component({
    selector: 'app-get-advice',
    imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    DragDropModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    FileControlComponent
],
    templateUrl: './get-advice.component.html',
    styleUrls: ['./get-advice.component.scss']
})
export class GetAdviceComponent {
  form: FormGroup<GetAdviceFormControls>;
  filteredAgents: User[] = [];
  agentsList: User[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<GetAdviceComponent>,
    private readonly agentsService: AgentsService,
    @Inject(MAT_DIALOG_DATA) public data: GetAdviceDialogData
  ) {
    this.form = this.fb.group<GetAdviceFormControls>({
      destinataire: this.fb.control<User | null>({ value: this.data ? this.data.destinataireId as any : null, disabled: !!this.data }, Validators.required),
      note: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required, noWhitespaceValidator] }),
      files: this.fb.control<File[] | null>(null)
    });
    // Récupère la liste complète des agents
    this.agentsService.getAllAgentsDemandes('consultation').subscribe((agents) => {
      this.agentsList = agents;
      this.filteredAgents = [...this.agentsList];
    });
    // Si un destinataireId est fourni, on le convertit en User complet
    if (this.data?.destinataireId) {
      const found = this.agentsList.find(a => a.id === this.data.destinataireId);
      if (found) {
        this.form.get('destinataire')?.setValue(found);
      } else {
        // fallback : appel API si l'agent n'était pas dans la liste
        this.agentsService.getAgentById(this.data.destinataireId).subscribe((agent) => {
          if (agent) this.form.get('destinataire')?.setValue(agent);
        });
      }
    }
  }
  effaceAgentFiltreRecrutement(event: Event): void {
    event.stopPropagation();
    this.form.controls['destinataire'].setValue(null);
    this.filteredAgents = [...this.agentsList];
  }

  agentdisplayFnRecrutement(agent: User | null): string {
    return agent ? `${agent.nom} ${agent.prenom}` : '';
  }

  agentFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (typeof value !== 'string') {
      this.filteredAgents = [];
      return;
    }
    const filterValue = value.toLowerCase().split(' ').join('');
    this.filteredAgents = this.agentsList.filter((agent: User) =>
      (agent.nom + agent.prenom).toLowerCase().split(' ').join('').includes(filterValue)
    );
  }

  onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const formValue = this.form.getRawValue();
    const result: GetAdviceResult = {
      destinataireId: formValue.destinataire?.id || '',
      note: formValue.note,
      files: formValue.files
    };
    this.dialogRef.close(result);
  }
}
